/**
 * NotificationsBell — Sidebar bell icon with unread badge + dropdown of recent events.
 *
 * Phase 5.5 scaffold: in-memory feed. Future phases can swap the data source to
 * a `app_notifications` Supabase table without changing this component.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useIdioma, type Traducir } from '../i18n/idioma';
import { Bell, Check, X, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useNotifications, NotificationSeverity } from '../contexts/NotificationsContext';

const severityIcon = (s: NotificationSeverity) => {
    switch (s) {
        case 'danger': return <AlertTriangle size={14} className="text-peligro" />;
        case 'warn': return <AlertCircle size={14} className="text-aviso" />;
        default: return <Info size={14} className="text-navy-600" />;
    }
};

/** `t` por parámetro: función suelta, sin hook posible. */
const formatRelative = (ts: number, t: Traducir): string => {
    const diff = Date.now() - ts;
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return t('avisos.hace.ahorita');
    const min = Math.floor(sec / 60);
    if (min < 60) return t('avisos.hace.minutos', { n: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return t('avisos.hace.horas', { n: hr });
    const d = Math.floor(hr / 24);
    return t('avisos.hace.dias', { n: d });
};

const NotificationsBell: React.FC = () => {
    const { t } = useIdioma();
    const { notifications, unreadCount, markAllRead, clear, clearAll } = useNotifications();
    const [open, setOpen] = useState(false);
    const popRef = useRef<HTMLDivElement>(null);

    // Click-outside-to-close
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={popRef}>
            <button
                onClick={() => {
                    setOpen(o => !o);
                    if (!open && unreadCount > 0) {
                        // Mark read on open so the badge clears
                        setTimeout(() => markAllRead(), 1500);
                    }
                }}
                aria-label={unreadCount > 0 ? t('avisos.sinLeer', { count: unreadCount, n: unreadCount }) : t('avisos.titulo')}
                aria-expanded={open}
                className="relative p-2 text-acero-500 hover:text-navy-950 hover:bg-acero-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                title={unreadCount > 0 ? t('avisos.sinLeer', { count: unreadCount, n: unreadCount }) : t('avisos.titulo')}
            >
                <Bell size={18} strokeWidth={2} />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-brand-400 rounded-full ring-2 ring-white" />
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-acero-200 rounded-2xl shadow-s3 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 border-b border-acero-200 flex justify-between items-center">
                        <h4 className="font-semibold text-navy-950 text-sm">{t('notificaciones.panel.titulo')}</h4>
                        {notifications.length > 0 && (
                            <button
                                onClick={clearAll}
                                className="text-xs text-acero-500 hover:text-navy-950 font-medium rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                            >
                                {t('notificaciones.panel.borrar_todo')}
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center text-sm text-acero-500">
                                <Check size={28} className="mx-auto mb-2 text-exito" />
                                {t('notificaciones.panel.vacio')}
                            </div>
                        ) : (
                            <ul className="divide-y divide-acero-100">
                                {notifications.map(n => (
                                    <li key={n.id} className={`group px-4 py-3 hover:bg-acero-50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-brand-50/40' : ''}`}>
                                        <div className="mt-0.5 shrink-0">{severityIcon(n.severity)}</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-navy-950 leading-snug">{n.body}</p>
                                            <p className="font-data text-[10px] text-acero-400 mt-1">{formatRelative(n.timestamp, t)}</p>
                                        </div>
                                        <button
                                            onClick={() => clear(n.id)}
                                            className="rp-accion-fila text-acero-300 hover:text-navy-950 p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                            title={t('notificaciones.panel.quitar_tooltip')}
                                            aria-label={t('notificaciones.panel.quitar_aria')}
                                        >
                                            <X size={12} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
